import { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { ShieldCheck, Buildings, ToggleLeft, Receipt, Users, Camera, ChartBar, FloppyDisk, ArrowRight, Sparkle } from "@phosphor-icons/react";
import McKinseyButton from "@/components/McKinseyButton";
import LoadFailedPanel from "@/components/LoadFailedPanel";

function StatusDot({ status }) {
  const tone = status === "ok" ? "bg-emerald-500" : status === "configured" ? "bg-emerald-500"
    : status === "missing" ? "bg-amber-400" : status === "fail" ? "bg-rose-500" : "bg-[var(--border-strong)]";
  return <span data-testid={`status-dot-${status}`} className={`inline-block w-2 h-2 rounded-full ${tone}`} />;
}

function Tile({ label, value, sub, accent = "text-[#263776]", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[var(--border)] rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-1">{label}</div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function CommandCenter() {
  const { user } = useAuth();
  const can = (user?.ux_tier || 0) >= 5;
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    if (!can) return;
    setErr(null);
    setD(null);
    api.get("/admin/command-center").then(r => setD(r.data)).catch(e => setErr(e));
  }, [can]);

  useEffect(() => { load(); }, [load]);

  if (!can) return <div className="p-6 text-[var(--text-secondary)]">CEO only.</div>;
  if (err) return <div className="p-6"><LoadFailedPanel error={err} resource="the Command Center" onRetry={load} /></div>;
  if (!d) return <div className="p-6 text-[var(--text-muted)]">Loading command center…</div>;

  return (
    <div data-testid="command-center-page" className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
            <ShieldCheck size={28} className="text-[#F47D20]" />
            CEO Command Center
          </h1>
          <div className="text-xs text-[var(--text-muted)]">
            Generated {fmtDate(d.generated_at)} · One-screen overview of system health, governance and operations.
          </div>
        </div>
        <Link to="/admin/audit-trail" data-testid="cc-audit-link"
          className="text-xs px-3 py-1.5 bg-[#263776] text-white rounded hover:bg-[#1a2659] flex items-center gap-2">
          <ChartBar size={14} /> Audit-trail dashboard <ArrowRight size={12} />
        </Link>
      </div>

      {/* System Health */}
      <section data-testid="cc-system-health" className="bg-white border border-[var(--border)] rounded-lg p-5">
        <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">System health</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
          {Object.entries(d.system_health).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <StatusDot status={v} />
              <div>
                <div className="capitalize text-xs text-[var(--text-muted)]">{k.replace(/_/g, " ")}</div>
                <div className="font-mono text-[11px] text-[var(--text-primary)]">{v}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Counts at a glance */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Tile testid="cc-tile-cases" label="Cases" value={d.counts.cases} sub="active matters" />
        <Tile testid="cc-tile-clients" label="Clients" value={d.counts.clients} />
        <Tile testid="cc-tile-invoices" label="Invoices" value={d.counts.invoices} />
        <Tile testid="cc-tile-stakeholders" label="Stakeholders" value={d.counts.stakeholders} />
        <Tile testid="cc-tile-users" label="Users" value={d.counts.users_active} sub="active" />
        <Tile testid="cc-tile-evidence" label="Evidence" value={d.counts.evidence} />
        <Tile testid="cc-tile-sightings" label="Sightings" value={d.counts.sightings} />
        <Tile testid="cc-tile-recycle" label="Recycle bin" value={d.counts.recycle_bin_size} sub="730-day TTL" accent="text-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Backup */}
        <section data-testid="cc-backup" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-2 flex items-center gap-2"><FloppyDisk size={14} /> Backup</div>
          <div className="text-sm">
            <div className="text-[var(--text-secondary)]">Last verified: <strong>{d.backup.last_verified_at ? fmtDate(d.backup.last_verified_at) : "never"}</strong></div>
            <div className="text-[var(--text-muted)] text-xs mt-1">By {d.backup.last_verified_by || "—"} · {d.backup.total_verified} total</div>
            <div className="text-xs text-[var(--text-muted)] mt-2">Audit retention: <strong>{d.audit_retention_days} days</strong></div>
          </div>
        </section>

        {/* Feature flags */}
        <section data-testid="cc-flags" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-2 flex items-center gap-2"><ToggleLeft size={14} /> Feature flags</div>
          <div className="text-2xl font-bold text-[#263776]">{d.feature_flags.on} <span className="text-base text-[var(--text-faint)]">/ {d.feature_flags.total} on</span></div>
          <Link to="/settings/admin/feature-toggles" data-testid="cc-flags-link" className="text-xs text-[#F47D20] hover:underline">Manage flags →</Link>
        </section>

        {/* Email analytics */}
        <section data-testid="cc-email" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-2">Email (last 30d)</div>
          <div className="text-2xl font-bold text-[#263776]">{d.email_analytics.events_last_30d}</div>
          <div className="text-xs text-[var(--text-muted)]">events · {d.email_analytics.drip_campaigns_running} drip running · {d.email_analytics.suppressed_total} suppressed</div>
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top active cases */}
        <section data-testid="cc-top-cases" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">Top 5 active cases (last activity)</div>
          <ul className="space-y-2">
            {d.top_cases.length === 0 && <li className="text-xs text-[var(--text-faint)]">No active cases.</li>}
            {d.top_cases.map(c => (
              <li key={c.case_id} data-testid={`cc-case-${c.case_id}`} className="flex items-center justify-between gap-3 border-b border-[var(--border)] pb-2 last:border-b-0">
                <Link to={`/cases/${c.case_id}`} className="flex-1 min-w-0 hover:underline">
                  <div className="text-sm font-medium text-[var(--text-primary)] truncate">{c.case_code} · {c.title || "—"}</div>
                  <div className="text-[11px] text-[var(--text-muted)]">{c.client_name || "—"} · {c.status} · {c.priority || "—"} · RM {c.rm_initials || "—"}</div>
                </Link>
                <McKinseyButton sourceType="case" sourceId={c.case_id}
                  path={`/cases/${c.case_id}/mckinsey/narrative`} label="AI" />
              </li>
            ))}
          </ul>
        </section>

        {/* Recent deletion requests */}
        <section data-testid="cc-deletions" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">Last 10 deletion requests</div>
          <ul className="space-y-1.5">
            {d.deletion_requests.length === 0 && <li className="text-xs text-[var(--text-faint)]">No deletion requests.</li>}
            {d.deletion_requests.map(r => (
              <li key={r.request_id} data-testid={`cc-deletion-${r.request_id}`} className="text-xs flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                  r.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                  r.status === "rejected" ? "bg-rose-100 text-rose-700" :
                  "bg-amber-100 text-amber-700"
                }`}>{r.status}</span>
                <span className="text-[var(--text-muted)]">{r.resource_type}/{(r.resource_id || "").slice(-8)}</span>
                <span className="text-[var(--text-faint)] ml-auto">{r.created_at?.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Invited users */}
        <section data-testid="cc-invited" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3 flex items-center gap-2"><Users size={14} /> Last 10 invited users</div>
          <ul className="space-y-1.5">
            {d.invited_users.length === 0 && <li className="text-xs text-[var(--text-faint)]">No invitations yet. <Link to="/settings/admin/invite-user" className="text-[#F47D20] hover:underline">Invite one →</Link></li>}
            {d.invited_users.map(u => (
              <li key={u.user_id} data-testid={`cc-invited-${u.user_id}`} className="text-xs flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded bg-[var(--surface-3)] text-[var(--text-secondary)] text-[10px]">T{u.ux_tier}</span>
                <span className="font-medium">{u.name}</span>
                <span className="text-[var(--text-faint)]">{u.email}</span>
                {u.must_change_password && <span className="text-amber-600 text-[10px]">· pending rotation</span>}
              </li>
            ))}
          </ul>
        </section>

        {/* Branding preview */}
        <section data-testid="cc-branding" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">Branding preview</div>
          <div className="flex items-center gap-4">
            {d.branding.logo_url ?
              <img src={d.branding.logo_url} alt="logo" className="h-12 border border-[var(--border)] rounded p-1" /> :
              <div className="h-12 w-24 bg-[var(--surface-3)] rounded flex items-center justify-center text-[var(--text-faint)] text-xs">No logo</div>}
            <div className="flex gap-2">
              <span title="Primary" data-testid="cc-color-primary" style={{background: d.branding.primary_color}} className="w-8 h-8 rounded border border-[var(--border)]" />
              <span title="Accent" data-testid="cc-color-accent" style={{background: d.branding.accent_color}} className="w-8 h-8 rounded border border-[var(--border)]" />
            </div>
            <Link to="/settings/admin/branding" className="text-xs text-[#F47D20] hover:underline">Edit branding →</Link>
          </div>
          <div className="text-[10px] text-[var(--text-faint)] mt-3 truncate" title={d.branding.footer_legal}>{d.branding.footer_legal}</div>
        </section>
      </div>
    </div>
  );
}
